import { Colors, Heading, SearchBar } from '@amelio/ui-library';

import { GLASS_STACK_GAP, MEETINGS_TITLE, SEARCH_FIELD_WIDTH } from './meetingStyles';

import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type { Company } from '@/types/directory/Company';

import { CompanyAttributeFilters } from '@/components/CompanyAttributeFilters/CompanyAttributeFilters';
import { useAmelioTranslations } from '@/hooks';

interface MeetingsToolbarProps {
    searchQuery: string;
    onSearchChange: (query: string) => void;
    company: Company | undefined;
    onFiltersChange: (filters: SelectedFilters) => void;
}

export const MeetingsToolbar = ({
    searchQuery,
    onSearchChange,
    company,
    onFiltersChange,
}: MeetingsToolbarProps) => {
    const { t } = useAmelioTranslations();

    return (
        // Two stacked rows: the search field sits below the title, at the glass container's own
        // rhythm.
        <div className={`flex flex-col ${GLASS_STACK_GAP}`}>
            <Heading
                level={3}
                color={Colors.NAVY}
                className={MEETINGS_TITLE}
            >
                {t('thematicMeetings.list.yourMeetings')}
            </Heading>

            {/* Search field and the shared company-attribute filter picker on one row — the same
                composition `ObjectivesPage` already uses for `CompanyAttributeFilters` beside a
                search bar (`inline` renders the picked attributes' own chips as wrapping siblings,
                below the trigger, once something is selected). Every base attribute family is
                offered (job title, department, work location, employee level, manager, plus
                whatever custom company attributes exist), matching what the team's own
                EmployeeMeetings filter bar exposes for the same "filter the roster by employee
                attributes" need. */}
            <div className={'flex flex-wrap items-center gap-3'}>
                <div className={SEARCH_FIELD_WIDTH}>
                    <SearchBar
                        value={searchQuery}
                        onChange={(value) => onSearchChange(value)}
                        onClear={() => onSearchChange('')}
                        placeholder={t('thematicMeetings.list.searchPlaceholder')}
                        showClearButton
                        // `bg-white/70`: in this build `bg-white/80` loses to the field's own `bg-white`
                        // (see src/responsive-utilities.css).
                        className={'rounded-xl bg-white/70'}
                        ariaLabel={t('thematicMeetings.list.searchPlaceholder')}
                        dataTestId={'meetings-search'}
                    />
                </div>

                <CompanyAttributeFilters
                    company={company}
                    onFiltersChange={onFiltersChange}
                    inline
                />
            </div>
        </div>
    );
};
